import React, { useEffect, useState } from 'react';

import { useGetOne, usePermissions } from 'react-admin';

import {
  Card,
  CardContent,
  Box,
  CardHeader,
  LinearProgress,
  Typography,
  Divider,
} from '@mui/material';

import ReactApexChart from 'react-apexcharts';
import { isEmpty } from 'lodash';
import SelectInputButton from '../../layout/inputFields/SelectInputButton';
import hasAccess from '../../../utilities/hasAccess';
import actions, { Actions } from '../../../constants/actions';
import useHoverSeriesUpdateLabel from '../../../hooks/useHoverSeriesUpdateLabel';
import theme from '../../../theme';

const useGetGraphData = (
  data: {
    accounts: number;
    businesses: number;
    cars: number;
    individuals: number;
    products: number;
  } | undefined,
  chartRef: React.RefObject<ReactApexChart>,
) => {
  const { permissions } = usePermissions<Actions[]>();
  const [hasData, setHasData] = useState(false);
  const [options, setOptions] = useState<ReactApexChart['props']['options']>();
  const [series, setSeries] = useState<ReactApexChart['props']['series']>();

  const {
    getTotalOptions,
    attachLegendHoverEvents,
  } = useHoverSeriesUpdateLabel({ chartRef });

  useEffect(() => {
    if (isEmpty(data)) return;

    const newSeries = [
      ...(hasAccess(permissions, actions.ACCOUNT_ENTITY_GET) ? [data.accounts] : []),
      ...(hasAccess(permissions, actions.BUSINESS_GET) ? [data.businesses] : []),
      ...(hasAccess(permissions, actions.INDIVIDUAL_GET) ? [data.individuals] : []),
      ...(hasAccess(permissions, actions.PRODUCT_GET) ? [data.products] : []),
      ...(hasAccess(permissions, actions.CAR_GET) ? [data.cars] : []),
    ];

    const labels = [
      ...(hasAccess(permissions, actions.ACCOUNT_ENTITY_GET) ? ['Accounts'] : []),
      ...(hasAccess(permissions, actions.BUSINESS_GET) ? ['Businesses'] : []),
      ...(hasAccess(permissions, actions.INDIVIDUAL_GET) ? ['Individuals'] : []),
      ...(hasAccess(permissions, actions.PRODUCT_GET) ? ['Products'] : []),
      ...(hasAccess(permissions, actions.CAR_GET) ? ['Cars'] : []),
    ];

    const colors = ['#238061', '#1F487E', '#F3B31D', '#D32C2C', '#B8DBD9'];

    const totalOptions = getTotalOptions({
      series: newSeries,
      colors,
      labels,
      totalColor: theme.palette.primary.main,
      totalLabel: 'Total entities',
    });

    const newOptions = {
      colors,
      labels,
      chart: {
        redrawOnParentResize: true,
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        events: {
          mounted: attachLegendHoverEvents,
          updated: attachLegendHoverEvents,
        },
      },
      legend: {
        position: 'bottom',
        show: newSeries?.some((v) => v > 0),
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                ...totalOptions,
              },
            },
          },
        },
      },
    } satisfies ReactApexChart['props']['options'];

    setHasData(true);
    setOptions(newOptions);
    setSeries(newSeries);
  }, [data, permissions, attachLegendHoverEvents, getTotalOptions]);

  return {
    hasData,
    options,
    series,
  };
};

const EntitiesDistribution = () => {
  const [period, setPeriod] = useState('30');

  const chartRef = React.useRef<ReactApexChart>(null);
  const { data, isLoading } = useGetOne('dashboard', { id: `entities-distribution/${period}` });
  const { hasData, options, series } = useGetGraphData(data, chartRef);

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        height: '100%',
      }}
    >
      <CardHeader
        title="Created entities"
        action={(
          <SelectInputButton
            choices={[
              { id: '1', name: 'Last day' },
              { id: '7', name: 'Last week' },
              { id: '30', name: 'Last month' },
              { id: '90', name: 'Last quarter' },
              { id: '365', name: 'Last year' },
              { id: '99999', name: 'All time' },
            ]}
            value={period}
            setValue={setPeriod}
          />
        )}
      />
      <Divider />
      <CardContent
        sx={{
          flexGrow: 1,
        }}
      >
        {isLoading && <LinearProgress /> }
        {hasData && <ReactApexChart ref={chartRef} options={options} series={series} type="donut" height={300} />}
        {(!isLoading && !hasData) && (
          <Box margin={10} width="100%">
            <Typography align="center"> No entities available </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EntitiesDistribution;
